/**
 * DialogProvider — promise-based custom popups replacing window.confirm/alert/prompt.
 * useDialog() returns { confirm, alert, prompt } so call sites keep imperative flow:
 *   if (!(await confirm({ message: '…' }))) return
 * One dialog active at a time; overlay click cancels (confirm→false, prompt→null). A dialog that another one
 * replaces is answered the same way (alert→void), so a caller awaiting it is never left hanging.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useT } from '../../i18n/language-provider'
import { ConfirmDialog, type ConfirmText } from './confirm-dialog'
import { AlertResultDialog } from './alert-result-dialog'
import { Button } from './button'
import { giveFocusBack } from './focus-return'

export interface ConfirmOptions {
  title?: string
  message: ReactNode
  confirmLabel?: string
  destructive?: boolean
  confirmText?: ConfirmText
  /** `lg` widens the card for a message that is a list; omitted = the narrow card every caller had. */
  size?: 'sm' | 'lg'
}

export interface AlertOptions {
  message: ReactNode
  variant?: 'success' | 'error'
}

export interface PromptOptions {
  title: string
  defaultValue?: string
  /** The longest answer the field takes - what the asker's api would refuse past, so it is never typed or pasted in the first place. */
  maxLength?: number
}

interface DialogApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  alert: (opts: AlertOptions) => Promise<void>
  prompt: (opts: PromptOptions) => Promise<string | null>
}

type DialogBody =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'alert'; opts: AlertOptions; resolve: () => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void }
/** A dialog on screen: its body and a number of its own, so that one that replaces another is drawn afresh (focus, typed text). */
type ActiveDialog = DialogBody & { serial: number }

const DialogContext = createContext<DialogApi | null>(null)

export function useDialog(): DialogApi {
  const ctx = useContext(DialogContext)
  if (!ctx) throw new Error('useDialog must be used within DialogProvider')
  return ctx
}

const PROMPT_CONTROLS = 'input:not([disabled]), button:not([disabled])'

function PromptDialog({ title, defaultValue, maxLength, onSubmit, onCancel }: PromptOptions & { onSubmit: (v: string) => void; onCancel: () => void }) {
  const t = useT()
  const [value, setValue] = useState(defaultValue ?? '')
  const card = useRef<HTMLFormElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const cancel = useRef(onCancel)
  cancel.current = onCancel
  // A click on the backdrop cancels only if it BEGAN there: selecting the name and letting go of the mouse outside the card is no click away.
  const pressedOnBackdrop = useRef(false)

  useEffect(() => {
    // Taken before the field is focused, so the focus can go back to what had it (autoFocus would have moved it already).
    const previous = document.activeElement as HTMLElement | null
    input.current?.focus()
    // On the window, before anything else sees the key: Escape and Tab are this dialog's wherever the focus has fallen to (Shift+Tab out of the
    // card, the page behind it), and a page's own Escape (a list over the canvas that closes on it) must not act on the same key and leave this
    // dialog standing over what is left. Escape cancels - what was typed is dropped, as the browser's own prompt did - but not while an input
    // method is composing: that Escape ends the composition (Vietnamese, Chinese, Japanese typing), and the dialog stays. The key goes no
    // further then either (the page behind would close its list and leave this dialog over the canvas), but is not prevented: the input
    // method ends the composition by itself. WebKit sends the keydown that ends a composition after the composition has ended, so
    // `isComposing` is already false on it; it is marked by keyCode 229.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (e.isComposing || e.keyCode === 229) { e.stopPropagation(); return }
        e.preventDefault()
        e.stopPropagation()
        cancel.current()
        return
      }
      if (e.key !== 'Tab' || !card.current) return
      const controls = [...card.current.querySelectorAll<HTMLElement>(PROMPT_CONTROLS)]
      if (controls.length === 0) return
      const first = controls[0]!
      const last = controls[controls.length - 1]!
      const active = document.activeElement
      if (!card.current.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus() }
      else if (e.shiftKey && (active === first || active === card.current)) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      giveFocusBack(previous)
    }
  }, [])

  return (
    <div
      id="prompt-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => { pressedOnBackdrop.current = e.target === e.currentTarget }}
      onClick={(e) => { if (pressedOnBackdrop.current && e.target === e.currentTarget) onCancel() }}
    >
      <form
        ref={card}
        tabIndex={-1}
        className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onSubmit(value) }}
      >
        <h2 id="prompt-dialog-title" className="mb-3 text-base font-semibold text-fg">{title}</h2>
        <input
          id="prompt-dialog-input"
          ref={input}
          aria-labelledby="prompt-dialog-title"
          maxLength={maxLength}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mb-5 w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="md" id="prompt-dialog-cancel-btn" type="button" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="default" size="md" id="prompt-dialog-ok-btn" type="submit">
            {t('common.ok')}
          </Button>
        </div>
      </form>
    </div>
  )
}

/** Answers a dialog nobody got to answer: what its cancel button would have said. */
function dismiss(dialog: ActiveDialog | null): void {
  if (!dialog) return
  if (dialog.kind === 'confirm') dialog.resolve(false)
  else if (dialog.kind === 'alert') dialog.resolve()
  else dialog.resolve(null)
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const t = useT()
  const [active, setActive] = useState<ActiveDialog | null>(null)
  const current = useRef<ActiveDialog | null>(null)
  const serial = useRef(0)

  const show = useCallback((body: DialogBody) => {
    dismiss(current.current)
    const next: ActiveDialog = { ...body, serial: ++serial.current }
    current.current = next
    setActive(next)
  }, [])
  const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => show({ kind: 'confirm', opts, resolve })), [show])
  const alert = useCallback((opts: AlertOptions) => new Promise<void>((resolve) => show({ kind: 'alert', opts, resolve })), [show])
  const prompt = useCallback((opts: PromptOptions) => new Promise<string | null>((resolve) => show({ kind: 'prompt', opts, resolve })), [show])

  // Only the dialog that is still on screen leaves it: a late click on one that was just replaced (before React drew the
  // newer one) must answer its own caller and not take the newer dialog down with it.
  const settle = (dialog: ActiveDialog, fn: () => void) => {
    if (current.current === dialog) { current.current = null; setActive(null) }
    fn()
  }

  return (
    <DialogContext.Provider value={{ confirm, alert, prompt }}>
      {children}
      {active?.kind === 'confirm' && (
        <ConfirmDialog
          key={active.serial}
          title={active.opts.title ?? t('common.confirm')}
          message={active.opts.message}
          confirmLabel={active.opts.confirmLabel ?? t('common.ok')}
          destructive={active.opts.destructive}
          confirmText={active.opts.confirmText}
          size={active.opts.size}
          onConfirm={() => settle(active, () => active.resolve(true))}
          onCancel={() => settle(active, () => active.resolve(false))}
        />
      )}
      {active?.kind === 'alert' && (
        <AlertResultDialog
          key={active.serial}
          variant={active.opts.variant ?? 'error'}
          message={active.opts.message}
          onClose={() => settle(active, () => active.resolve())}
        />
      )}
      {active?.kind === 'prompt' && (
        <PromptDialog
          key={active.serial}
          title={active.opts.title}
          defaultValue={active.opts.defaultValue}
          maxLength={active.opts.maxLength}
          onSubmit={(v) => settle(active, () => active.resolve(v))}
          onCancel={() => settle(active, () => active.resolve(null))}
        />
      )}
    </DialogContext.Provider>
  )
}
